import { AxiosError } from "axios";
import { Button, Container, Form, Grid, Input, Link, Stack, Text } from "basis";
import { getBpayBiller } from "library/api";
import { ApiGetResponse } from "library/api/bpay";
import { validateBpayBillerCode } from "library/formValidation";
import { useAccountOptions } from "library/hooks/useAccountOptions";
import { useEffect } from "react";
import { useMutation } from "react-query";
import { AccountsSelect } from "../AccountsSelect/AccountsSelect";
import { SvgInline } from "../SvgInline/SvgInline";
import { EVENTS } from "./bpayReducer";
import { GetBpayBillerParams } from "./types";

export const BpayLanding = ({ state, dispatch }) => {
  const { isLoading, isError, accountOptions } = useAccountOptions();
  const initialValues = {
    fromAccount:
      state.fromAccount?.id ??
      (accountOptions.length === 1 ? accountOptions[0].value : ""),
    biller: state.biller,
  };
  const getBillerMutation = useMutation<
    ApiGetResponse,
    AxiosError,
    GetBpayBillerParams
  >(getBpayBiller, {
    onSuccess: (response) => {
      const { biller } = response.data;

      dispatch({
        type: EVENTS.BILLER_VERIFIED_SUCCESSFULLY,
        biller,
      });
    },
    onError: (error, { setErrors }) => {
      const errorType = error.response?.data?.type ?? null;

      switch (errorType) {
        case "INVALID_BILLER_CODE": {
          setErrors({
            bsb: "Invalid biller code",
          });
          break;
        }

        default: {
          dispatch({
            type: EVENTS.BILLER_VERIFICATION_FAILED,
            errorType,
          });
        }
      }
    },
  });
  const onSubmit = ({ errors, values, setErrors }) => {
    const { biller } = values;

    if (Object.keys(errors).length === 0) {
      getBillerMutation.mutate({
        billerCode: biller.code.replace(/\s/g, ""),
        setErrors,
      });
    }
  };

  useEffect(() => {
    if (isError) {
      dispatch({
        type: EVENTS.FAILED_TO_LOAD_ACCOUNT_OPTIONS,
      });
    } else if (!isLoading && accountOptions.length === 0) {
      dispatch({
        type: EVENTS.NO_ACCOUNTS,
      });
    }
  }, [accountOptions, dispatch, isLoading, isError]);

  if (isError) {
    return null;
  }

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      {({ state: formState }) => {
        const selectedAccountOption = accountOptions.find(
          (accountOption) => accountOption.value === formState.values.toAccount,
        )?.data;
        const isOverdue = selectedAccountOption?.isOverdue === true;

        return (
          <Stack gap="8">
            <Grid cols="4" cols-sm="8" colsGap="30px" rowsGap="2">
              <Grid.Item colSpan="all" colSpan-sm="0-1">
                <Text>
                  <strong>From account</strong>
                </Text>
              </Grid.Item>
              <Grid.Item colSpan="all" colSpan-sm="2-7">
                <Container bg="grey.t05" padding="4">
                  <AccountsSelect
                    name="fromAccount"
                    label="From account"
                    hideLabel
                    accountOptions={accountOptions}
                    accountFooter={({ data }) =>
                      data.isOverdue ? (
                        <Container bg="grey.t05" padding="2 4">
                          <Text
                            color="conditional.negative.text"
                            textStyle="body2"
                          >
                            Currently unavailable for BPay
                          </Text>
                        </Container>
                      ) : null
                    }
                    onChange={({ selectedOption }) => {
                      dispatch({
                        type: EVENTS.ACCOUNT_SELECTED,
                        fromAccount: {
                          id: selectedOption.value,
                          data: selectedOption.data,
                          cardToken: selectedOption.cardToken,
                        },
                      });
                    }}
                  />
                </Container>
              </Grid.Item>
            </Grid>
            <Grid cols="4" cols-sm="8" colsGap="30px" rowsGap="2">
              {isOverdue ? null : (
                <Grid.Item colSpan="all" colSpan-sm="0-1">
                  <Text>
                    <strong>Pay to</strong>
                  </Text>
                </Grid.Item>
              )}
              <Grid.Item colSpan="all" colSpan-sm="2-7">
                {isOverdue ? (
                  <Stack gap="8">
                    <SvgInline name="direct-debit-payment-cancel" size="92" />
                    <Text textStyle="heading5" textStyle-sm="heading4">
                      Sorry, we’re unable to proceed with your BPay payment
                    </Text>
                    <Text>We’ve noticed that your account is overdue.</Text>
                    <Text>
                      Please make a{" "}
                      <Link href="/ways-to-pay" newTab={false}>
                        one time payment
                      </Link>{" "}
                      to get it up to date before proceeding with a BPay
                      payment.
                    </Text>
                  </Stack>
                ) : (
                  <Stack gap="8">
                    <Container bg="grey.t05" padding="4">
                      <Container width="100%" width-xs="50%">
                        <Input
                          name="biller.code"
                          label="Biller code"
                          variant="numeric"
                          validate={validateBpayBillerCode}
                        />
                      </Container>
                    </Container>
                    <Button
                      type="submit"
                      width="160"
                      loading={getBillerMutation.isLoading}
                    >
                      Next
                    </Button>
                  </Stack>
                )}
              </Grid.Item>
            </Grid>
          </Stack>
        );
      }}
    </Form>
  );
};
